import React, { useState } from 'react';
import Button from './Button';

const iconBase = {
  fill: 'none', stroke: 'currentColor', strokeWidth: 1.7,
  strokeLinecap: 'round', strokeLinejoin: 'round',
};

const SlidersIcon = (p) => (
  <svg viewBox="0 0 24 24" width="13" height="13" {...iconBase} {...p}>
    <path d="M4 8h9M17 8h3M4 16h3M11 16h9" />
    <circle cx="15" cy="8" r="2" />
    <circle cx="9" cy="16" r="2" />
  </svg>
);

const EditIcon = (p) => (
  <svg viewBox="0 0 24 24" width="13" height="13" {...iconBase} {...p}>
    <path d="M4 20h4L19 9l-4-4L4 16v4Z" />
    <path d="M14 6l4 4" />
  </svg>
);

const Chevron = ({ dir = 'down', ...p }) => (
  <svg viewBox="0 0 24 24" width="11" height="11" {...iconBase} {...p}>
    {dir === 'down' ? <path d="M6 9l6 6 6-6" /> : <path d="M6 15l6-6 6 6" />}
  </svg>
);

const InfoIcon = (p) => (
  <svg viewBox="0 0 24 24" width="12" height="12" {...iconBase} {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
);

const MinusIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...iconBase} {...p}>
    <path d="M5 12h14" />
  </svg>
);

const PlusIcon = (p) => (
  <svg viewBox="0 0 24 24" width="14" height="14" {...iconBase} {...p}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const ResetIcon = (p) => (
  <svg viewBox="0 0 24 24" width="12" height="12" {...iconBase} {...p}>
    <path d="M3 3v5h5" />
    <path d="M4 13a8 8 0 1 0 2.3-6.3L3 8" />
  </svg>
);

/**
 * Collapsed-by-default control to override the auto-suggested starting page — for an
 * existing paper minute book's opening balance, or a correction. Intent is explicit:
 * this sets the OPENING page for the year (applied only when this is the first minutes
 * recorded for the scope), not a per-PDF renumber.
 *
 * The auto-suggested page is shown by PageContinuityBanner above, so the collapsed
 * states here never repeat it — they only offer the affordance (auto) or surface +
 * undo the manual value (manual).
 *
 * `value` is the override (or null to use the suggestion); `onChange(n|null)` reports it.
 */
export default function StartPageOverride({ suggestedStartPage = 1, value, onChange, lockedByContinuity = false }) {
  const [open, setOpen] = useState(false);
  const effective = Number.isInteger(value) ? value : suggestedStartPage;
  const isManual = Number.isInteger(value) && value !== suggestedStartPage;

  // A positive integer sets the override; anything else (empty / 0 / NaN) clears
  // back to auto. Shared by the typed input and the − / + stepper buttons.
  const commit = (n) => onChange(Number.isInteger(n) && n > 0 ? n : null);

  // ── Collapsed + manual: amber recap so the override stays visible & undoable ──
  // When continuity is locked the backend ignores the override, so swap the
  // wording (and drop Edit) — the chip must not claim a start page the PDF
  // won't actually use.
  if (!open && isManual) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 10px',
          borderRadius: 9,
          border: '1px solid #f0d9a0',
          background: '#fff7e6',
          color: '#8a6a14',
          fontFamily: "'Outfit', sans-serif",
          fontSize: 11.5,
        }}
      >
        <EditIcon style={{ flexShrink: 0 }} />
        <span>
          {lockedByContinuity ? (
            <>Manual page <strong style={{ fontWeight: 700 }}>{value}</strong> — won&apos;t apply (sequence in progress)</>
          ) : (
            <>Manual start at page <strong style={{ fontWeight: 700 }}>{value}</strong></>
          )}
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 10 }}>
          {!lockedByContinuity && <LinkBtn tone="amber" onClick={() => setOpen(true)}>Edit</LinkBtn>}
          <LinkBtn tone="amber" onClick={() => onChange(null)}>Reset to auto</LinkBtn>
        </span>
      </div>
    );
  }

  // ── Collapsed + auto: quiet dashed affordance ────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 10px',
          borderRadius: 9,
          border: '1px dashed #d8d1c0',
          background: 'transparent',
          color: '#7a7060',
          fontFamily: "'Outfit', sans-serif",
          fontSize: 11.5,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'color .18s, border-color .18s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#1e8a8a';
          e.currentTarget.style.color = '#145f5f';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#d8d1c0';
          e.currentTarget.style.color = '#7a7060';
        }}
      >
        <SlidersIcon style={{ flexShrink: 0 }} />
        Set a custom starting page
        <Chevron dir="down" style={{ marginLeft: 'auto', flexShrink: 0 }} />
      </button>
    );
  }

  // ── Expanded: contained editor ───────────────────────────────────────────────
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 10,
        border: '1px solid #e8e3d8',
        background: '#faf9f6',
        fontFamily: "'Outfit', sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 9 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.6px',
            textTransform: 'uppercase',
            color: '#7a7060',
          }}
        >
          Custom starting page
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          title="Collapse"
          aria-label="Collapse"
          style={{
            display: 'inline-flex',
            background: 'none',
            border: 'none',
            color: '#a09880',
            cursor: 'pointer',
            padding: 2,
          }}
        >
          <Chevron dir="up" />
        </button>
      </div>

      <label
        htmlFor="start-page-input"
        style={{ display: 'block', fontSize: 11.5, color: '#5a5246', marginBottom: 6 }}
      >
        Start at page
      </label>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 9 }}>
        <div className="fc-stepper">
          <button
            type="button"
            className="fc-stepper__btn"
            onClick={() => commit(effective - 1)}
            disabled={effective <= 1}
            aria-label="Decrease start page"
          >
            <MinusIcon />
          </button>
          <input
            id="start-page-input"
            className="fc-stepper__input"
            type="number"
            min={1}
            inputMode="numeric"
            value={effective}
            onChange={(e) => commit(parseInt(e.target.value, 10))}
            aria-label="Start at page"
          />
          <button
            type="button"
            className="fc-stepper__btn"
            onClick={() => commit(effective + 1)}
            aria-label="Increase start page"
          >
            <PlusIcon />
          </button>
        </div>
        <Button variant="primary" size="sm" onClick={() => setOpen(false)}>
          Done
        </Button>
      </div>

      {isManual && (
        <button
          type="button"
          onClick={() => onChange(null)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 9,
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#145f5f',
            cursor: 'pointer',
            fontFamily: "'Outfit', sans-serif",
            fontSize: 11.5,
            fontWeight: 600,
          }}
        >
          Use suggested ({suggestedStartPage})
        </button>
      )}

      <div
        style={{
          display: 'flex',
          gap: 6,
          marginTop: 9,
          paddingTop: 9,
          borderTop: '1px solid #ece7da',
          fontSize: 10.5,
          color: lockedByContinuity ? '#8a4214' : '#9a8a5a',
          lineHeight: 1.45,
        }}
      >
        <InfoIcon style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          {lockedByContinuity
            ? 'Sequence already in progress for this year — the override will not be applied. Reset to auto to continue from the suggested page.'
            : 'One-time opening balance — applied only when this is the first minutes recorded for the year.'}
        </span>
      </div>
    </div>
  );
}

// Small underline text-button used inside the amber recap row.
function LinkBtn({ tone, onClick, children }) {
  const color = tone === 'amber' ? '#8a6a14' : '#145f5f';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color,
        cursor: 'pointer',
        fontFamily: "'Outfit', sans-serif",
        fontSize: 11.5,
        fontWeight: 600,
        textDecoration: 'underline',
        textUnderlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}
